import React from 'react';
import {
  Typography,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import EmptyState from '../EmptyState';
import { formatCurrency, formatNumber, formatQuantity } from './formatters';

interface ComparativeData {
  categoria: string;
  valor_total: number;
  icms_total: number;
  quantidade_total: number;
  total_transacoes: number;
}

interface DrillDownComparativeAnalysisProps {
  analise_comparativa: ComparativeData[];
  title?: string;
  showPercentage?: boolean;
}

const DrillDownComparativeAnalysis: React.FC<DrillDownComparativeAnalysisProps> = ({ 
  analise_comparativa, 
  title = "Análise Comparativa - Perdas vs Outras Operações",
  showPercentage = true 
}) => {
  if (!analise_comparativa || analise_comparativa.length === 0) {
    return (
      <Grid item xs={12}>
        <EmptyState
          icon="📊"
          title="Análise Comparativa Indisponível"
          description="Não há dados suficientes para realizar a análise comparativa"
          height={200}
        />
      </Grid>
    );
  }

  const valorTotalGeral = analise_comparativa.reduce((sum, item) => sum + item.valor_total, 0);

  return (
    <Grid item xs={12}>
      <Typography variant="subtitle1" gutterBottom sx={{ mt: 3, fontWeight: 600 }}>
        📊 {title}
      </Typography>
      
      <TableContainer component={Paper} sx={{ mt: 2, borderRadius: 2, boxShadow: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Categoria</strong></TableCell>
              <TableCell align="right"><strong>Valor Total</strong></TableCell>
              <TableCell align="right"><strong>ICMS Total</strong></TableCell>
              <TableCell align="right"><strong>Quantidade</strong></TableCell>
              <TableCell align="right"><strong>Transações</strong></TableCell>
              {showPercentage && (
                <TableCell align="right"><strong>% do Total</strong></TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {analise_comparativa.map((categoria, index) => {
              const percentual = valorTotalGeral > 0 ? ((categoria.valor_total / valorTotalGeral) * 100) : 0;
              const isLosses = categoria.categoria === 'Perdas';
              
              return (
                <TableRow key={index} hover sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                  <TableCell>
                    <Chip 
                      label={categoria.categoria}
                      color={
                        isLosses ? 'error' : 
                        categoria.categoria === 'Saídas' ? 'warning' : 
                        categoria.categoria === 'Entradas' ? 'success' : 'default'
                      }
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography 
                      variant="body2" 
                      fontWeight={isLosses ? 600 : 500}
                      color={isLosses ? 'error.main' : 'text.primary'}
                    >
                      {formatCurrency(categoria.valor_total)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography 
                      variant="body2"
                      color={isLosses ? 'error.main' : 'text.primary'}
                    >
                      {formatCurrency(categoria.icms_total)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {formatQuantity(categoria.quantidade_total)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {formatNumber(categoria.total_transacoes)}
                    </Typography>
                  </TableCell>
                  {showPercentage && (
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        color={isLosses && percentual > 5 ? 'error.main' : 'text.primary'}
                        fontWeight={isLosses ? 600 : 400}
                      >
                        {percentual.toFixed(1)}%
                      </Typography>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Alerta para perdas elevadas */}
      {analise_comparativa.some(item => {
        const percentual = valorTotalGeral > 0 ? ((item.valor_total / valorTotalGeral) * 100) : 0;
        return item.categoria === 'Perdas' && percentual > 5;
      }) && (
        <Paper sx={{ p: 2, mt: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
          <Typography variant="body2" fontWeight={600}>
            ⚠️ Atenção: As perdas representam mais de 5% do valor total movimentado
          </Typography>
        </Paper>
      )}
    </Grid>
  );
};

export default DrillDownComparativeAnalysis; 